'use client';

import { useState, useMemo } from 'react';
import Image from 'next/image';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';

// Components
import Modal from '@src/components/modal';
import Button from '@src/components/atoms/button';
import ModalSearch from 'src/components/homepage/form-sell-rent-property-v2/_components/modal-search.js';
import ModalPropertyType from 'src/components/homepage/form-sell-rent-property-v2/_components/modal-property-type.js';
import ModalPropertyCategory from 'src/components/homepage/form-sell-rent-property-v2/_components/modal-property-category.js';
import ModalContact from 'src/components/homepage/form-sell-rent-property-v2/_components/modal-contact.js';

// Assets
import XIcon from '@public/png/x-1.png';
import SuccessConsignment from '@public/png/success-consignment.png';
import SuccessConsignment2 from '@public/png/success-consignment-2.png';

// Api
import { useSubmitPropertyConsignment } from '@src/apis/shared/mutation/post-property-consignment';
import handleError from '@src/helper/handleError';
import useSlack from '@src/hooks/useSlack';
import useWhatsapp from '@src/hooks/useWhatsapp';
import useGlobalCtx from '@src/context/global-ctx';

const StepLabel = ({ children, open = false }) =>
  open && (
    <span className="py-1 px-2 rounded-[2px] bg-[#DEDFDE] text-[11px] text-[#686A68]">
      {children}
    </span>
  );

const FormSellRentProperty = ({ isOpen, onClose, ...props }) => {
  const { custProfile } = useGlobalCtx();

  const { mutate: submitPropertyConsignment } = useSubmitPropertyConsignment();
  const { sendMessage } = useSlack();
  const { sendMessage: sendWhatsappMessage } = useWhatsapp();

  const [step, setStep] = useState(1);
  const [location, setLocation] = useState(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const [selectedPropertyCategory, setSelectedPropertyCategory] =
    useState(null);
  const [marketingData, setMarketingData] = useState(null);

  const [formData, setFormData] = useState({
    property_type_id: 1,
    property_category_id: null,
    name: custProfile?.name ?? '',
    phone: custProfile?.phone ?? '',
    property_location: null,
    company_id: null,
    city_id: null,
    area_id: null,
  });

  const handleSelectLocation = (location) => {
    let newForm = { ...formData };
    if (location?.slug === 'Area') {
      newForm.area_id = location.id;
      newForm.city_id = null;
    } else if (location?.slug === 'City') {
      newForm.city_id = location.id;
      newForm.area_id = null;
    }

    newForm.property_location = location.name;
    setFormData(newForm);
    setLocation(location);
  };

  const handleSelectPropertyType = (propertyType) => {
    let newForm = { ...formData };
    newForm.property_category_id = propertyType.id;
    setSelectedPropertyType(propertyType);
    setFormData(newForm);
  };

  const handleSelectPropertyCategory = (propertyCategory) => {
    let newForm = { ...formData };
    newForm.property_category_id = propertyCategory.id;
    setSelectedPropertyCategory(propertyCategory);
    setFormData(newForm);
  };

  const handleSendWhatsapp = () => {
    let msg = `Halo Bapak/Ibu ${marketingData?.full_name},\n\nPerkenalkan saya ${formData.name}, saya mau menjual/menyewakan Rumah di <location>\n\nTerimas kasih.`;

    const area = location?.slug === 'Area' ? `Area ${location.name}` : '';
    const city = location?.slug === 'City' ? `City ${location.name}` : '';

    if (location?.slug === 'Area') {
      msg = msg.replace('<location>', area);
    } else if (location?.slug === 'City') {
      msg = msg.replace('<location>', city);
    }

    sendWhatsappMessage({
      phone: marketingData?.phone,
      type: "button",
      message: msg,
    });
  };

  const handleSubmit = () => {
    setStep(5);
    const payload = {
      sale_rent_form: { ...formData },
    };

    if (!!formData.area_id) {
      delete payload.sale_rent_form.city_id;
    } else if (!!formData.city_id) {
      delete payload.sale_rent_form.area_id;
    }

    if (formData.property_category_id === 3) {
      delete payload.sale_rent_form.property_category_id;
    }

    if (!formData.company_id) {
      delete payload.sale_rent_form.company_id;
    }

    const slackPayload = {
      ...formData,
      property_type: formData.property_type_id === 1 ? 'Dijual' : 'Disewa',
      property_category: selectedPropertyCategory?.name,
      page: window.location.href,
    };

    submitPropertyConsignment(payload, {
      onSuccess: async (response) => {
        setMarketingData(response?.data?.data);
        await sendMessage(slackPayload);
        setStep(6);
      },
      onError: (error) => {
        toast.error(handleError(error));
        setStep(4);
      },
    });
  };

  const RenderContent = () => {
    switch (step) {
      case 1:
        return (
          <ModalSearch
            location={location}
            onSelectLocation={handleSelectLocation}
          />
        );
      case 2:
        return (
          <ModalPropertyType
            selectedPropertyType={selectedPropertyType}
            onSelectPropertyType={handleSelectPropertyType}
          />
        );
      case 3:
        return (
          <ModalPropertyCategory
            selectedPropertyCategory={selectedPropertyCategory}
            onSelectPropertyCategory={handleSelectPropertyCategory}
          />
        );
      case 4:
        return (
          <ModalContact
            formData={formData}
            setFormData={setFormData}
            selectedPropertyCategory={selectedPropertyCategory}
            onSelectPropertyCategory={handleSelectPropertyCategory}
          />
        );
      default:
        return <div>Default</div>;
    }
  };

  const handleNext = () => {
    if (step === 4) {
      handleSubmit();
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const disabledNext = useMemo(() => {
    switch (step) {
      case 1:
        return !formData.property_location;
      case 2:
        return !selectedPropertyType;
      case 3:
        return !selectedPropertyCategory;
      case 4:
        return !formData.name || !formData.phone;
      default:
        return false;
    }
  }, [step, formData, selectedPropertyType, selectedPropertyCategory]);

  return (
    <Modal {...props} isOpen={isOpen} onClick={onClose}>
      {step === 6 ? (
        <div
          className="bg-white-3 z-[42] rounded-2xl w-[452px] py-[12px]"
        >
          <div className="flex justify-between items-center px-6 pt-2 pb-2 border-b border-dashed border-[#D8D8D9]">
            <Image
              onClick={onClose}
              src={XIcon}
              alt="Close"
              width="auto"
              height="auto"
              className="w-4 h-4 cursor-pointer object-contain"
            />
          </div>

          <Image
            src={SuccessConsignment2}
            alt="Success Consignment"
            width={200}
            height={200}
            className="mx-auto mb-2"
          />

          <p className="font-satoshi-bold text-center text-[21px] leading-[25px] text-[#252526]">
            Berhasil terhubung ke marketing
          </p>
          <p className="font-satoshi-regular text-center text-[14px] leading-[20px] text-[#686A68]">
            Terhubung ke WhatsApp marketing. Anda dapat melanjutkan diskusi
            dengan marketing properti kami kapan saja.
          </p>

          <div className="flex justify-end items-center px-6 pb-2 pt-2 border-t border-dashed border-[#D8D8D9]">
            <Button
              type="button"
              onClick={handleSendWhatsapp}
              className="px-4 py-2 bg-[#252526] text-white rounded-lg flex items-center gap-2"
            >
              <FaWhatsapp className="text-white" /> Hubungi Marketing
            </Button>
          </div>
        </div>
      ) : step === 5 ? (
        <div className="bg-white-3 z-[42] rounded-2xl w-[452px] py-[12px]">
          <Image
            src={SuccessConsignment}
            alt="Success Consignment"
            width={200}
            height={200}
            className="mx-auto mb-2"
          />

          <p className="font-satoshi-bold text-center text-[21px] leading-[25px] text-[#252526]">
            Menghubungkan ke marketing...
          </p>
          <p className="font-satoshi-regular text-center text-[14px] leading-[20px] text-[#686A68]">
            Mohon tunggu dan jangan tutup halaman ini. Kami sedang menyiapkan
            percakapan WhatsApp dengan marketing yang sesuai dengan lokasi
            properti Anda.
          </p>
        </div>
      ) : (
        <div
          className="bg-white-3 z-[42] rounded-2xl w-[560px]
          dd2-xl:w-screen dd2-xl:h-full dd2-xl:rounded-[0px] overflow-auto scrollbar-hide dd2-xl:flex dd2-xl:flex-col dd2-xl:relative"
        >
          <div>
            <div className="flex justify-between items-center px-6 pt-4 pb-2 border-b border-dashed border-[#D8D8D9]">
              <Image
                onClick={onClose}
                src={XIcon}
                alt="Close"
                width="auto"
                height="auto"
                className="w-4 h-4 cursor-pointer object-contain"
              />
              <p className="font-satoshi-bold text-[14px] leading-[19.6px] text-[#686A68]">
                Langkah <span className="text-[#252526]">{step}</span> dari 4
              </p>
            </div>
            {step >= 2 && step <= 4 && (
              <div className="flex items-center gap-2 px-6 py-2 border-b border-dashed border-[#D8D8D9]">
                <StepLabel open={step >= 2}>{location?.name}</StepLabel>
                <StepLabel open={step >= 3}>
                  {selectedPropertyType?.name}
                </StepLabel>
                <StepLabel open={step >= 4}>
                  {selectedPropertyCategory?.name}
                </StepLabel>
              </div>
            )}

            {RenderContent()}
          </div>
          <div
            className="flex justify-end items-center px-6 pb-4 pt-2 bg-white border-t border-dashed border-[#D8D8D9] 
             dd2-xl:justify-center dd2-xl:gap-4 dd2-xl:absolute dd2-xl:bottom-0 dd2-xl:left-0 dd2-xl:right-0 dd2-xl:w-full dd2-xl:z-[43]"
          >
            {step > 1 && (
              <Button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 bg-[#252526] bg-transparent !text-[#252526] underline 
                dd2-xl:w-full"
              >
                Kembali
              </Button>
            )}
            <Button
              type="button"
              disabled={disabledNext}
              onClick={handleNext}
              className="px-4 py-2 bg-[#252526] text-white rounded-lg dd2-xl:w-full"
            >
              Lanjut
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  return <FormSellRentProperty isOpen={isOpen} {...props} />;
};

export default Wrapper;
